import {
  Empty,
  EmptyDescription,
  EmptyHeader,
  EmptyMedia,
  EmptyTitle,
  ScrollArea,
} from "@devhop/ui";
import {
  Facebook,
  FileText,
  Instagram,
  Loader2,
  MessageCircle,
  Music,
  ShoppingCart,
  Sparkles,
} from "lucide-react";
import { resolveImageSrc } from "@/shared/ui/AppImage";
import type { CartItem } from "../context/CartContext";
import { useCustomerDisplayCustomer } from "../hooks/useCustomerDisplayCustomer";
import { useCustomerDisplayItems } from "../hooks/useCustomerDisplayItems";
import { useCustomerDisplayPaymentProgress } from "../hooks/useCustomerDisplayPaymentProgress";

type CustomerCartItemProps = {
  item: CartItem;
};

function CustomerCartItem({ item }: CustomerCartItemProps) {
  const unitPrice =
    item.price + item.modifiers.reduce((sum, mod) => sum + mod.priceDelta, 0);

  return (
    <div className="border-border/50 border-b pt-4 pb-4 last:border-b-0">
      <div className="flex items-start justify-between gap-4">
        <div className="min-w-0 flex-1">
          <div className="flex flex-wrap items-baseline gap-3">
            <h3 className="font-semibold text-xl">{item.productName}</h3>
            {item.note && (
              <span className="text-muted-foreground text-sm">(Note)</span>
            )}
            <span className="text-base text-muted-foreground">
              × {item.quantity}
            </span>
          </div>
          {item.variantName && (
            <p className="mt-1 text-base text-muted-foreground">
              {item.variantName}
            </p>
          )}
          {item.modifiers.length > 0 && (
            <div className="mt-2 space-y-1">
              {item.modifiers.map((modifier) => (
                <div
                  key={`${modifier.modifierId}-${modifier.optionValue}`}
                  className="text-base text-muted-foreground"
                >
                  <span className="text-foreground">+</span>{" "}
                  {modifier.modifierName}:{" "}
                  <span className="font-medium">{modifier.optionLabel}</span>
                  {modifier.priceDelta !== 0 && (
                    <span className="ml-1">
                      ({modifier.priceDelta > 0 ? "+" : ""}
                      {modifier.priceDelta.toLocaleString("lo-LA")} ₭)
                    </span>
                  )}
                </div>
              ))}
            </div>
          )}
          {item.note && (
            <div className="mt-2 flex items-start gap-2 rounded bg-muted/50 px-3 py-2">
              <FileText className="mt-0.5 h-4 w-4 shrink-0 text-muted-foreground" />
              <p className="text-muted-foreground text-sm italic leading-relaxed">
                {item.note}
              </p>
            </div>
          )}
        </div>
        <div className="shrink-0 text-right">
          <p className="font-bold text-2xl text-primary">
            {item.totalPrice.toLocaleString("lo-LA")} ₭
          </p>
          {item.quantity > 1 && (
            <p className="mt-1 text-base text-muted-foreground">
              {unitPrice.toLocaleString("lo-LA")} ₭ each
            </p>
          )}
        </div>
      </div>
    </div>
  );
}

/** Aspect ratio 4:5 สำหรับ QR ธนาคาร */
const QR_ASPECT_RATIO = "aspect-[4/5]";

type CustomerCartViewProps = {
  paymentQrUrls?: string[];
  socialMediaUrls?: {
    whatsapp?: string | null;
    facebook?: string | null;
    instagram?: string | null;
    tiktok?: string | null;
  };
};

export function CustomerCartView({
  paymentQrUrls = [],
  socialMediaUrls,
}: CustomerCartViewProps) {
  const { items, total, discountTotal, discounts } = useCustomerDisplayItems();
  const { paymentProgress } = useCustomerDisplayPaymentProgress();
  const { customer: memberCustomer } = useCustomerDisplayCustomer();
  const hasDiscount = discountTotal > 0 || discounts.length > 0;
  const hasQr = paymentQrUrls.length > 0;
  const showSplitProgress = Boolean(paymentProgress?.isSplit);
  const showRemainingDue = Boolean(
    paymentProgress &&
      paymentProgress.remainingDue > 0 &&
      (paymentProgress.isSplit || paymentProgress.paidTotal > 0),
  );
  const qrPayAmount =
    paymentProgress?.qrPayAmount != null && paymentProgress.qrPayAmount > 0
      ? paymentProgress.qrPayAmount
      : null;
  const hasSocialMedia =
    socialMediaUrls &&
    (socialMediaUrls.whatsapp ||
      socialMediaUrls.facebook ||
      socialMediaUrls.instagram ||
      socialMediaUrls.tiktok);

  return (
    <div className="flex h-full flex-col lg:flex-row">
      {/* Cart section */}
      <div className="flex min-w-0 flex-1 flex-col">
        <ScrollArea className="flex-1 overflow-auto">
          <div className="p-4">
            {items.length === 0 ? (
              <div className="flex min-h-[50vh] items-center justify-center lg:min-h-[40vh]">
                <Empty>
                  <EmptyHeader>
                    <EmptyMedia variant="icon">
                      <ShoppingCart className="h-16 w-16 text-muted-foreground" />
                    </EmptyMedia>
                    <EmptyTitle className="text-2xl">Empty Cart</EmptyTitle>
                    <EmptyDescription className="text-base">
                      Please wait for staff to select items
                    </EmptyDescription>
                  </EmptyHeader>
                </Empty>
              </div>
            ) : (
              <div className="space-y-2">
                {items.map((item) => (
                  <CustomerCartItem key={item.id} item={item} />
                ))}
              </div>
            )}
          </div>
        </ScrollArea>

        {(items.length > 0 || memberCustomer) && (
          <div className="border-t bg-muted/30 p-6">
            {memberCustomer && (
              <div className="mb-3 flex items-center justify-between gap-4 border-border/60 border-b pb-3">
                <p className="min-w-0 truncate font-semibold text-lg">
                  {memberCustomer.displayName}
                </p>
                <div className="flex shrink-0 items-center justify-end gap-2">
                  <Sparkles className="h-4 w-4 text-amber-600 dark:text-amber-400" />
                  <span className="text-muted-foreground text-sm">
                    ຄະແນນສະສົມ
                  </span>
                  {memberCustomer.loyaltyPointsBalance === null ? (
                    <Loader2 className="h-5 w-5 animate-spin text-amber-700 dark:text-amber-300" />
                  ) : (
                    <span className="font-bold text-2xl text-amber-800 tabular-nums dark:text-amber-200">
                      {memberCustomer.loyaltyPointsBalance.toLocaleString(
                        "lo-LA",
                      )}
                    </span>
                  )}
                </div>
              </div>
            )}
            {hasDiscount && (
              <div className="mb-3 space-y-2">
                {discounts.length > 0 ? (
                  discounts.map((d) => (
                    <div
                      key={d.id}
                      className="flex items-center justify-between gap-4"
                    >
                      <span className="text-lg text-muted-foreground">
                        {d.label}
                      </span>
                      <span className="font-medium text-green-600 text-lg dark:text-green-400">
                        -{d.amount.toLocaleString("lo-LA")} ₭
                      </span>
                    </div>
                  ))
                ) : (
                  <div className="flex items-center justify-between gap-4">
                    <span className="text-lg text-muted-foreground">ສ່ວນຫຼຸດ</span>
                    <span className="font-medium text-green-600 text-lg dark:text-green-400">
                      -{discountTotal.toLocaleString("lo-LA")} ₭
                    </span>
                  </div>
                )}
              </div>
            )}
            {items.length > 0 && (
              <div className="space-y-2">
                <div className="flex items-center justify-between gap-4">
                  <span className="font-semibold text-xl">
                    {showSplitProgress ? "ຍອດລວມທັງບິນ:" : "Total:"}
                  </span>
                  <span className="font-bold text-3xl text-primary">
                    {total.toLocaleString("lo-LA")} ₭
                  </span>
                </div>

                {showRemainingDue && paymentProgress && (
                  <div className="flex items-center justify-between gap-4 rounded-lg bg-primary/10 px-4 py-3">
                    <span className="font-semibold text-lg">ຄົງເຫຼືອ:</span>
                    <span className="font-bold text-2xl text-primary">
                      {paymentProgress.remainingDue.toLocaleString("lo-LA")} ₭
                    </span>
                  </div>
                )}
              </div>
            )}

            {items.length > 0 && showSplitProgress && paymentProgress && (
              <div className="mt-4 space-y-3 border-border/60 border-t pt-4">
                {paymentProgress.paidTotal > 0 && (
                  <div className="flex items-center justify-between gap-4">
                    <span className="text-lg text-muted-foreground">
                      ຊຳລະແລ້ວ:
                    </span>
                    <span className="font-medium text-lg">
                      {paymentProgress.paidTotal.toLocaleString("lo-LA")} ₭
                    </span>
                  </div>
                )}

                {paymentProgress.rows.length > 0 && (
                  <div className="space-y-2">
                    {paymentProgress.rows.map((row) => (
                      <div
                        key={row.id}
                        className="flex items-center justify-between gap-4 text-base"
                      >
                        <span className="text-muted-foreground">
                          • {row.methodName}
                        </span>
                        <span className="font-medium text-green-600 dark:text-green-400">
                          {row.amount.toLocaleString("lo-LA")} ₭ ✓
                        </span>
                      </div>
                    ))}
                  </div>
                )}

                {paymentProgress.remainingDue <= 0 && paymentProgress.isComplete ? (
                  <p className="text-center font-semibold text-green-600 text-lg dark:text-green-400">
                    ຊຳລະຄົບແລ້ວ
                  </p>
                ) : null}

                {paymentProgress.isComplete && paymentProgress.change > 0 && (
                  <div className="flex items-center justify-between gap-4">
                    <span className="text-lg text-muted-foreground">
                      ເງິນທອນ:
                    </span>
                    <span className="font-semibold text-green-600 text-lg dark:text-green-400">
                      {paymentProgress.change.toLocaleString("lo-LA")} ₭
                    </span>
                  </div>
                )}
              </div>
            )}
          </div>
        )}
      </div>

      {/* QR section - พื้นที่เฉพาะสำหรับ QR ขนาดใหญ่ */}
      {hasQr && (
        <div className="flex shrink-0 flex-col border-t bg-muted/20 p-6 lg:min-w-[320px] lg:justify-start lg:border-t-0 lg:border-l">
          <p className="mb-4 text-center font-semibold text-foreground text-lg">
            Scan Payment
          </p>
          {qrPayAmount != null && (
            <p className="mb-4 text-center font-bold text-primary text-xl">
              ກະລຸນາຊຳລະ {qrPayAmount.toLocaleString("lo-LA")} ₭
            </p>
          )}
          <div className="flex flex-wrap items-center justify-center gap-6">
            {paymentQrUrls.map((url, i) => (
              <div
                key={url}
                className={`flex w-[180px] shrink-0 overflow-hidden rounded-lg border-2 border-border bg-white shadow-sm sm:w-[200px] lg:w-[220px] ${QR_ASPECT_RATIO}`}
              >
                <img
                  src={resolveImageSrc(url) ?? undefined}
                  alt={`Payment QR ${i + 1}`}
                  className="size-full object-contain p-2"
                />
              </div>
            ))}
          </div>

          {/* Social Media Links */}
          {hasSocialMedia && (
            <div className="mt-auto border-t pt-6">
              <p className="mb-4 text-center font-semibold text-foreground text-sm">
                Follow Us
              </p>
              <div className="flex max-w-md flex-wrap items-center justify-center gap-3">
                {socialMediaUrls?.whatsapp && (
                  <a
                    href={`https://wa.me/${socialMediaUrls.whatsapp.replace(/[^0-9]/g, "")}`}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="flex items-center gap-2 rounded-lg border border-border bg-background px-4 py-2 text-sm transition-colors hover:bg-muted"
                  >
                    <MessageCircle className="h-4 w-4 text-green-600" />
                    <span>{socialMediaUrls.whatsapp}</span>
                  </a>
                )}
                {socialMediaUrls?.facebook && (
                  <a
                    href={`https://facebook.com/${socialMediaUrls.facebook}`}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="flex items-center gap-2 rounded-lg border border-border bg-background px-4 py-2 text-sm transition-colors hover:bg-muted"
                  >
                    <Facebook className="h-4 w-4 text-blue-600" />
                    <span>{socialMediaUrls.facebook}</span>
                  </a>
                )}
                {socialMediaUrls?.instagram && (
                  <a
                    href={`https://instagram.com/${socialMediaUrls.instagram}`}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="flex items-center gap-2 rounded-lg border border-border bg-background px-4 py-2 text-sm transition-colors hover:bg-muted"
                  >
                    <Instagram className="h-4 w-4 text-pink-600" />
                    <span>{socialMediaUrls.instagram}</span>
                  </a>
                )}
                {socialMediaUrls?.tiktok && (
                  <a
                    href={`https://tiktok.com/@${socialMediaUrls.tiktok}`}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="flex items-center gap-2 rounded-lg border border-border bg-background px-4 py-2 text-sm transition-colors hover:bg-muted"
                  >
                    <Music className="h-4 w-4 text-black" />
                    <span>{socialMediaUrls.tiktok}</span>
                  </a>
                )}
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
}
